import React, { useEffect, useState } from "react";
import { observer, useLocalStore } from "mobx-react";
import { Alert, Button, Checkbox, Spin, Tooltip } from "antd";
import { InfoCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";

import BaseModal from "@app/components/modal";
import confirm from "@app/components/confirm";
import http from "@app/lib/http";
import notify from "@app/components/notify";
import cerConfigStore from "@app/state/store/cer-config";
import { aiTemplateDocumentName, aiTemplateShortName } from "@app/constants";

import {
    GENERAL_SECTION,
    fieldBlocks,
    fromConfig,
    incompleteFields,
    mergeFromConfig,
    missingItemLabel,
    missingRequired,
    toPayload,
} from "./fields";
import { resolveTemplate } from "./templates";
import FieldList from "./field-list";
import SectionPanel from "./section-panel";
import CopyFrom from "./copy-from";
import alertIcon from "./alert-icon";

import "./style.scoped.scss";
import "./modal.scss";

const GENERAL_STEP = "general";
const SECTIONS_STEP = "sections";
const DETAILS_STEP = "details";

const REQUIRED_FIELDS_MESSAGE = "Please fill in all required fields before continuing.";
const NO_SECTION_MESSAGE = "Select at least one section to continue.";
const INCOMPLETE_ROWS_MESSAGE = "Some rows are incomplete. Complete or remove them before saving.";

const SectionSelect = observer(({ template, values, setValue }) => {
    const selected = Array.isArray(values.sections_selected) ? values.sections_selected : [];
    const selectedSet = new Set(selected);

    const toggle = (key) => {
        setValue(
            "sections_selected",
            selectedSet.has(key) ? selected.filter((k) => k !== key) : [...selected, key],
        );
    };

    const hasStoredConfig = (key) =>
        Boolean(
            (values.section_documents?.[key] || []).length ||
                (values.section_articles?.[key] || []).length ||
                (values.section_priority?.[key] || []).length,
        );

    return (
        <div className="cer-section-select">
            <div className="intro">
                Choose what to generate. The next step asks only for the input each of these
                sections needs.
            </div>
            {template.sectionCatalog.map((s) => (
                <div className="section-row" key={s.key} onClick={() => toggle(s.key)}>
                    <Checkbox
                        checked={selectedSet.has(s.key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggle(s.key)}
                    />
                    <span className="label">{s.label}</span>
                    {hasStoredConfig(s.key) && (
                        <span className="retained">Previous configuration available</span>
                    )}
                </div>
            ))}
        </div>
    );
});

const PageHeader = ({ pages, pageIndex, actions = null }) => (
    <div className="wizard-page-head">
        <div className="page-bars">
            {pages.map((page, i) => (
                <span
                    key={page.key}
                    className={`page-bar${i < pageIndex ? " done" : ""}${
                        i === pageIndex ? " active" : ""
                    }`}
                />
            ))}
        </div>
        <div className="page-heading">
            <h3>{pages[pageIndex].title}</h3>
            <span className="page-actions">
                {actions}
                <span className="page-count">
                    {pageIndex + 1} of {pages.length}
                </span>
            </span>
        </div>
    </div>
);

const CerConfigModal = observer(() => {
    const localState = useLocalStore(() => ({
        values: {},
        initializedFor: null,
        invalid: [],
    }));

    const [documents, setDocuments] = useState(null);
    const [articles, setArticles] = useState({ slr: null, sota: null });
    const projectId = cerConfigStore.projectId;
    const reportDocumentId = cerConfigStore.reportDocumentId;
    const visible = cerConfigStore.visible;
    const template = resolveTemplate(cerConfigStore.template);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setDocuments(null);
            if (!projectId || !visible) {
                return;
            }
            try {
                const { data } = await http.get(`/project/${projectId}/documents`);
                const list = (data?.list || []).map((d) => ({
                    _id: d._id,
                    name: d.name,
                    hasPdf: d.file?.mime === "application/pdf" || !!d.preview,
                    previewStatus: d.previewStatus,
                }));
                if (!cancelled) {
                    setDocuments(list);
                }
            } catch (ex) {
                if (!cancelled) {
                    setDocuments([]);
                }
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [projectId, visible]);

    useEffect(() => {
        let cancelled = false;
        async function loadChannel(type) {
            try {
                const {
                    data,
                } = await http.get(
                    `/project/${projectId}/report-documents/${reportDocumentId}/cer/articles`,
                    { type },
                );
                return data?.list || [];
            } catch (ex) {
                return [];
            }
        }
        async function load() {
            setArticles({ slr: null, sota: null });
            if (!projectId || !reportDocumentId || !visible) {
                return;
            }
            if (!template.hasArticles) {
                setArticles({ slr: [], sota: [] });
                return;
            }
            const [slr, sota] = await Promise.all([loadChannel("slr"), loadChannel("sota")]);
            if (!cancelled) {
                setArticles({ slr, sota });
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [projectId, reportDocumentId, visible, template.id, template.hasArticles]);

    if (!visible) {
        return null;
    }

    const cycleKey = `${projectId}:${cerConfigStore.reportDocumentId}:${
        cerConfigStore.config?.updated || ""
    }`;
    if (localState.initializedFor !== cycleKey) {
        localState.values = fromConfig(cerConfigStore.config, template);
        localState.initializedFor = cycleKey;
    }

    const setValue = (name, value) => {
        localState.values = { ...localState.values, [name]: value };
        if (localState.invalid.includes(name)) {
            localState.invalid = localState.invalid.filter((n) => n !== name);
        }
    };

    const applySource = (config, sectionKey = null) => {
        localState.values = mergeFromConfig(localState.values, config, template, { sectionKey });
    };

    const steps = template.steps;
    const stepIndex = Math.min(cerConfigStore.currentStepIndex, steps.length - 1);
    const currentStep = steps[stepIndex];
    const isLastStep = stepIndex === steps.length - 1;

    const selectedSet = new Set(
        Array.isArray(localState.values.sections_selected)
            ? localState.values.sections_selected
            : [],
    );
    const selectedSections = template.sectionCatalog
        .filter((s) => selectedSet.has(s.key))
        .map((s) => s.key);

    const sectionLabel = (key) => template.sectionCatalog.find((s) => s.key === key)?.label || key;

    const pagesOf = (stepKey) => {
        if (stepKey === GENERAL_STEP) {
            return fieldBlocks(template, GENERAL_SECTION).map((block) => ({
                key: block.id,
                title: template.blockTitles[block.id] || "General Info",
                fields: block.fields,
            }));
        }
        if (stepKey === DETAILS_STEP) {
            return selectedSections.map((key) => ({
                key,
                title: sectionLabel(key),
                fields: template.fields.filter((f) => f.section === key),
            }));
        }
        return [];
    };

    const pages = pagesOf(currentStep.key);
    const pageIndex = Math.min(cerConfigStore.currentPageIndex, Math.max(pages.length - 1, 0));
    const currentPage = pages[pageIndex] || null;
    const isLastPage = pageIndex >= pages.length - 1;

    const rejectPage = (index, page, invalidNames, message = REQUIRED_FIELDS_MESSAGE) => {
        localState.invalid = invalidNames;
        cerConfigStore.setStep(index, page);
        notify.warn(message);
        return false;
    };

    const validatePage = (index, page) => {
        const target = pagesOf(steps[index].key)[page];
        if (!target) {
            return true;
        }
        const missingNames = missingRequired(target.fields, localState.values);
        const incompleteNames = incompleteFields(target.fields, localState.values).filter(
            (name) => !missingNames.includes(name),
        );
        const invalidNames = [...missingNames, ...incompleteNames];
        return invalidNames.length ? rejectPage(index, page, invalidNames) : true;
    };

    const validateComplete = () => {
        for (let index = 0; index < steps.length; index++) {
            const stepPages = pagesOf(steps[index].key);
            for (let page = 0; page < stepPages.length; page++) {
                const incompleteNames = incompleteFields(stepPages[page].fields, localState.values);
                if (incompleteNames.length) {
                    return rejectPage(index, page, incompleteNames, INCOMPLETE_ROWS_MESSAGE);
                }
            }
        }
        return true;
    };

    const validateStep = (index) => {
        const step = steps[index];
        if (step.key === SECTIONS_STEP) {
            if (selectedSections.length === 0) {
                cerConfigStore.setStep(index);
                notify.warn(NO_SECTION_MESSAGE);
                return false;
            }
            return true;
        }
        return pagesOf(step.key).every((_, page) => validatePage(index, page));
    };

    const validateThrough = (from, to) => {
        for (let index = from; index < to; index++) {
            if (!validateStep(index)) {
                return false;
            }
        }
        return true;
    };

    const knownArticleIds =
        articles.slr && articles.sota
            ? [...articles.slr, ...articles.sota].map((a) => a._id)
            : null;

    const saveDraft = async ({ announce }) => {
        if (!validateComplete()) {
            return false;
        }
        const payload = toPayload(
            localState.values,
            documents && documents.map((d) => d._id),
            knownArticleIds,
            template,
        );
        const ok = await cerConfigStore.save(payload, { announce });
        if (ok) {
            localState.initializedFor = null;
        }
        return ok;
    };

    const onSave = () => saveDraft({ announce: true });

    const autosave = () => saveDraft({ announce: false });

    const onCancel = () => cerConfigStore.close();

    const goPrev = () => {
        if (pageIndex > 0) {
            cerConfigStore.setPage(pageIndex - 1);
            return;
        }
        const previous = Math.max(0, stepIndex - 1);
        const lastPage = Math.max(pagesOf(steps[previous].key).length - 1, 0);
        cerConfigStore.setStep(previous, lastPage);
    };

    const goNext = async () => {
        if (!isLastPage) {
            if (validatePage(stepIndex, pageIndex) && (await autosave())) {
                cerConfigStore.setPage(pageIndex + 1);
            }
            return;
        }
        if (validateStep(stepIndex) && (await autosave())) {
            cerConfigStore.setStep(Math.min(steps.length - 1, stepIndex + 1));
        }
    };

    const goToStep = async (index) => {
        if (index <= stepIndex) {
            cerConfigStore.setStep(index);
            return;
        }
        if (validateThrough(stepIndex, index) && (await autosave())) {
            cerConfigStore.setStep(index);
        }
    };

    const reportMissing = () => {
        const labels = (cerConfigStore.validation?.missing || []).map((item) =>
            missingItemLabel(template, item),
        );
        notify.error(
            [cerConfigStore.startRunBlockedReason, labels.join(", ")].filter(Boolean).join(" "),
        );
    };

    const onSaveAndRun = async () => {
        if (!validateThrough(0, steps.length)) {
            return;
        }
        const ok = await onSave();
        if (!ok) {
            return;
        }
        if (!cerConfigStore.canStartRun) {
            reportMissing();
            return;
        }
        const shortName = aiTemplateShortName(cerConfigStore.template);
        const rerun = cerConfigStore.hasPreviousRun;
        const proceed = await confirm(
            rerun ? (
                <div>
                    <p>
                        This will regenerate the sections you selected and replace their current
                        content.
                    </p>
                    <ul style={{ listStyle: "disc", paddingLeft: 20 }}>
                        <li>Edits to the sections being regenerated will be lost.</li>
                        <li>Sections you did not select keep their content.</li>
                        <li>Manually added sections will be preserved.</li>
                    </ul>
                    <br />
                    <p>
                        <b>NOTE</b>: This action cannot be undone
                    </p>
                </div>
            ) : (
                `Start a new ${shortName} generation run for this project?`
            ),
            {
                type: rerun ? "danger" : "default",
                title: rerun ? `Re-run ${shortName} generation?` : `Start ${shortName} run`,
            },
        );
        if (proceed) {
            const run = await cerConfigStore.startRun();
            if (run) {
                cerConfigStore.close();
            }
        }
    };

    const canRun = !cerConfigStore.draft;

    const runTooltip = canRun ? (
        `Save the form and start a ${aiTemplateShortName(cerConfigStore.template)} run.`
    ) : (
        <div className="cer-run-blocked-tip">{cerConfigStore.startRunBlockedReason}</div>
    );

    const generalAdvisories = template.advisories?.(GENERAL_SECTION, localState.values) || [];

    const footer = (
        <div className="wizard-foot">
            <div className="left">
                <Button onClick={onCancel}>Close</Button>
            </div>
            {currentStep.key === DETAILS_STEP && (
                <div className="ai-disclaimer">
                    <InfoCircleOutlined />
                    <span>
                        AI-generated content may be inaccurate. Review and verify before use.
                    </span>
                </div>
            )}
            <div className="right">
                <Button disabled={stepIndex === 0 && pageIndex === 0} onClick={goPrev}>
                    Previous
                </Button>
                <Tooltip title="Your progress is also saved each time you move to the next step.">
                    <Button loading={cerConfigStore.saving} onClick={onSave}>
                        Save
                    </Button>
                </Tooltip>
                {!(isLastStep && isLastPage) && (
                    <Button type="primary" onClick={goNext}>
                        Next
                    </Button>
                )}
                {isLastStep && isLastPage && (
                    <Tooltip title={runTooltip} overlayClassName="cer-run-blocked-tooltip">
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            disabled={!canRun}
                            loading={cerConfigStore.saving || cerConfigStore.running}
                            onClick={onSaveAndRun}
                        >
                            Save &amp; Start Run
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    );

    return (
        <BaseModal
            title={`Generate ${aiTemplateDocumentName(cerConfigStore.template)} with AI`}
            visible={true}
            centered
            width={900}
            wrapClassName="cer-config-modal"
            onCancel={onCancel}
            footer={footer}
        >
            <Spin spinning={cerConfigStore.busy}>
                <div className="wizard">
                    <div className="wizard-head">
                        <div className="wizard-toolbar">
                            {steps.length > 1 && (
                                <div className="step-indicator">
                                    {steps.map((s, i) => (
                                        <div
                                            key={s.key}
                                            className={`step-pill${
                                                i === stepIndex ? " active" : ""
                                            }`}
                                            onClick={() => goToStep(i)}
                                        >
                                            {i + 1}. {s.title}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="wizard-actions">
                                <CopyFrom template={template} onApply={(c) => applySource(c)} />
                            </div>
                        </div>

                        {cerConfigStore.draft && (
                            <div className="draft-banner">
                                <b>This project is still a draft.</b> You can fill in and save the
                                configuration now. Publish the project and upload source documents
                                to start generation.
                            </div>
                        )}
                    </div>

                    <div className="wizard-body">
                        {currentStep.key === SECTIONS_STEP && (
                            <SectionSelect
                                template={template}
                                values={localState.values}
                                setValue={setValue}
                            />
                        )}

                        {currentStep.key !== SECTIONS_STEP && !currentPage && (
                            <div className="wizard-empty">
                                No sections selected. Go back to Step 2 and choose at least one
                                section.
                            </div>
                        )}

                        {currentPage && (
                            <PageHeader
                                pages={pages}
                                pageIndex={pageIndex}
                                actions={
                                    currentStep.key === DETAILS_STEP && (
                                        <CopyFrom
                                            template={template}
                                            sectionKey={currentPage.key}
                                            onApply={(c) => applySource(c, currentPage.key)}
                                        />
                                    )
                                }
                            />
                        )}

                        {currentPage &&
                            currentStep.key === GENERAL_STEP &&
                            generalAdvisories
                                .filter((a) => !a.field)
                                .map((a, i) => (
                                    <Alert
                                        key={i}
                                        className="advisory"
                                        showIcon
                                        icon={alertIcon(a.type)}
                                        {...a}
                                    />
                                ))}

                        {currentPage && currentStep.key === GENERAL_STEP && (
                            <FieldList
                                template={template}
                                section={GENERAL_SECTION}
                                block={currentPage.key}
                                hideBlockTitles
                                values={localState.values}
                                setValue={setValue}
                                selectedSections={selectedSections}
                                invalid={localState.invalid}
                                advisories={generalAdvisories.filter((a) => a.field)}
                            />
                        )}

                        {currentPage && currentStep.key === DETAILS_STEP && (
                            <SectionPanel
                                key={currentPage.key}
                                template={template}
                                sectionKey={currentPage.key}
                                values={localState.values}
                                setValue={setValue}
                                documents={documents}
                                articles={articles}
                                selectedSections={selectedSections}
                                invalid={localState.invalid}
                            />
                        )}
                    </div>
                </div>
            </Spin>
        </BaseModal>
    );
});

export default CerConfigModal;
